<template>
    <div
        ref="filters"
        class="uik-filters"
    >
        <transition name="uik-filters">
            <div
                v-if="open"
                ref="filters"
                class="uik-filters__container"
                :style="`right: ${positionRight}; bottom: ${positionBottom};`"
            >
                <div class="uik-filters__head">
                    <UikCheckbox
                        v-if="type !== 'date' && !loading && filteredOptionsList.length > 0"
                        :value="areAllSelected"
                        color="primary"
                        @change="selectAllOrNone()"
                    />
                    <h1>{{ title }} {{ $t("UIK.components.universal.table.filters.filter", "Filter") }}</h1>
                    <button
                        class="uik-filters__reset-btn"
                        :title="$t('UIK.components.universal.table.filters.reset', 'Reset')"
                        @click="resetFilters()"
                    >
                        <UikIcon :icon="['fas', 'undo-alt']"/>
                    </button>
                    <button
                        class="uik-filters__close-btn"
                        :title="$t('UIK.components.universal.table.filters.close', 'Close')"
                        @click="close()"
                    >
                        <Uikon icon="close"/>
                    </button>
                </div>
                <div class="uik-filters__content">
                    <UikSearch
                        v-if="type !== 'date' && type !== 'number' && !loading"
                        v-model="search"
                        clear
                    />
                    <div v-if="loading" class="uik-filters__loading">
                        <UikLoader
                            color="#D8DCE6"
                            type="dots"
                            :size="0.75"
                        />
                    </div>
                    <div v-if="!loading && type === 'number'" class="uik-filters__content-wrapper">
                        <UikSlider
                            v-model="selected"
                            hasInput="around"
                            :max="Number(data[remap.max])"
                            :min="Number(data[remap.min])"
                            :step="Number(step)"
                        />
                    </div>
                    <div v-if="!loading && type === 'list'" class="uik-filters__content-wrapper">
                        <UikCheckbox
                            v-for="item in filteredOptionsList"
                            :key="getListOptionValue('key', item)"
                            :class="{ selected: getListOptionValue('isSelected', item) }"
                            color="primary"
                            :value="getListOptionValue('isSelected', item)"
                            :label="getListOptionValue('label', item)"
                            :emit-on-create="false"
                            :description="getListOptionValue('description', item)"
                            @change="toggleSelected(item)"
                        />
                    </div>
                    <div v-if="!loading && type === 'userList'" class="uik-filters__content-wrapper">
                        <label
                            v-for="(item, index) in filteredOptionsList"
                            :key="index"
                            :for="_uid + '-' + item[remap.id]"
                            class="uik-filters__user-select-label"
                            :class="{ selected: selected.includes(item[remap.id])}">
                            <UikAvatar
                                :imageUrl="item[remap.avatar_url] ? item[remap.avatar_url] : ''"
                                :name="item[remap.name] ? item[remap.name] : nullOptionMessage || $t('UIK.components.universal.table.filters.none', 'None')"
                                :title="item[remap.email] || ''"
                                :color="selected.includes(item[remap.id]) ? 'primary' : ''"
                                size="small"
                            />
                            <input
                                :id="_uid + '-' + item[remap.id]"
                                style="display:none"
                                type="checkbox"
                                :value="item[remap.id]"
                                @input="toggleSelected(item)">
                        </label>
                    </div>
                    <div v-if="!loading && type === 'date'" class="uik-filters__content-wrapper">
                        <UikDatePicker
                            v-if="open"
                            v-model="selected"
                            class="uik-filters__date"
                            inline
                            range
                            :notBefore="data[remap.min]"
                            :notAfter="data[remap.max]"
                        />
                    </div>
                    <div v-if="!loading" class="uik-filters__bottom">
                        <UikButton
                            primary
                            :text="$t('UIK.components.universal.table.filters.apply', 'Apply')"
                            @click="confirm()"
                        />
                    </div>
                </div>
            </div>
        </transition>
        <UikOverlay
            :opacity="9"
            :visible="open"
            @close="close()"
        />
    </div>
</template>

<script>
/* eslint-disable brace-style */
export default {
    name: "UikFilters",
    props: {
        /**
         * The selected filter options
         */
        activeData: { type: [Array, Object, undefined], default: () => {} },
        /**
         * The filter options to display
         */
        data: { type: [Array, Object, undefined], default: () => {} },
        /**
         * Is the filter open?
         */
        open: { type: Boolean, required: true },
        /**
         * The filter type (list, userList, date)
         */
        type: { type: String, default: "list" },
        /**
         * The filter title to render in the header
         */
        title: { type: String, default: "" },
        /**
         * Is the filter loading?
         */
        isLoading: { type: [Boolean, null], default: null },
        /**
         * An object that defines which options fields should be remaped to what
         */
        remaps: { type: [Object, null], default: null },
        /**
         * Null value option title message
         */
        nullOptionMessage: { type: [String, null], default: null },
        /**
         * A step value for the slider (only for number filter)
         */
        step: { type: [Number, String, null], default: null }
    },
    data () {
        return {
            search: "",
            positionRight: "unset",
            positionBottom: "unset",
            selected: []
        }
    },
    computed: {
        dataIsAFlatArray () {
            if (Array.isArray(this.data) && this.data.length > 0) {
                return !Array.isArray(this.data[0]) && !(typeof this.data[0] === "object" && this.data[0] !== null)
            }
            return true
        },
        /**
         * An object containing remapvalues including custom ones
         * Default values are overwritten if custom values are provided
         */
        remap () {
            return {
                id: "id",
                min: "min",
                max: "max",
                title: "title",
                name: "name",
                label: "label",
                email: "email",
                avatar_url: "avatar_url",
                ...this.remaps
            }
        },
        /**
         * Filters the list of options according to search params and returns the filtered array
         */
        filteredOptionsList () {
            if (!this.data) return []
            if (this.search !== "" && ((this.type === "list" || this.type === "userList"))) {
                let search = this.search.toLowerCase()
                return this.data.filter(e => {
                    return (typeof e === "string" && e.toLowerCase().startsWith(search)) ||
                        (e[this.remap.title] && ("" + e[this.remap.title]).toLowerCase().startsWith(search)) ||
                        (e[this.remap.name] && ("" + e[this.remap.name]).toLowerCase().startsWith(search)) ||
                        (e[this.remap.label] && ("" + e[this.remap.label]).toLowerCase().startsWith(search)) ||
                        (e.__model_display_name && ("" + e.__model_display_name).toLowerCase().startsWith(search))
                })
            }
            return this.data
        },
        /**
         * Checks if all filters are selected and returns true if they are
         */
        areAllSelected () {
            if ((this.type === "list" || this.type === "userList") && this.selected.length === this.data.length) return true
            return false
        },
        /**
         * Gets the current loading status
         */
        loading () {
            if (this.isLoading !== null) return this.isLoading
            return this.data.length <= 0
        }
    },
    watch: {
        open () {
            this.positionContainer()
            this.setActiveState()
        },
        loading () {
            if (!this.loading) this.setActiveState()
        }
    },
    mounted () {
        window.addEventListener("resize", this.positionContainer)
    },
    beforeDestroy () {
        window.removeEventListener("resize", this.positionContainer)
    },
    methods: {
        /**
         * Extracts a certain filter option value from a filter option (for list filter options)
          */
        getListOptionValue (type, item) {
            // The key prop value
            if (type === "key") {
                if (this.dataIsAFlatArray) return item
                else if (item.hasOwnProperty("__model")) return item.__model + item[this.remap.id]
                else return item[this.remap.id]
            }
            // Is the option selected?
            else if (type === "isSelected") {
                if (this.dataIsAFlatArray) return this.selected.includes(item)
                else if (item.hasOwnProperty("__model")) {
                    return this.selected.some(e => e.id === item.id && e.__model === item.__model)
                } else return this.selected.includes(item[this.remap.id])
            }
            // The option label
            else if (type === "label") {
                if (this.dataIsAFlatArray && item !== null) return item
                else if (!this.dataIsAFlatArray && item[this.remap.title] !== null) return item[this.remap.title]
                else return this.nullOptionMessage || "None"
            } else if (type === "description") {
                if (!this.dataIsAFlatArray && item.hasOwnProperty("__model_display_name")) return item.__model_display_name
                else return ""
            }
        },
        /**
         * Sets the filter to their default state according to props
         */
        setActiveState () {
            if (this.type === "number" && this.activeData.length < 2) {
                this.selected = [this.data[this.remap.min], this.data[this.remap.max]]
            } else this.selected = [...this.activeData]
            this.positionContainer()
        },
        /**
         * Closes the filters and clears inputs
         */
        close () {
            this.search = ""
            this.selected = []
            this.$emit("close")
        },
        /**
         * Emits the close event with selected options as the payload and clears inputs
         */
        confirm () {
            this.search = ""
            if (this.areAllSelected) this.selected = []
            this.$emit("close", { filters: this.selected })
        },
        /**
         * Clears all selected options and emits the options
         */
        resetFilters () {
            this.selected = []
            this.confirm()
        },
        /**
         * Opsitions the filters in viewport so that they don't overflow
         */
        positionContainer () {
            this.positionRight = "unset"
            this.positionBottom = "unset"
            this.$nextTick(() => {
                if (this.$refs.filters) {
                    let filtersLeft = this.$refs.filters.offsetLeft
                    let filtersTop = this.$refs.filters.offsetTop
                    let filtersWidth = this.$refs.filters.offsetWidth
                    let filtersHeight = this.$refs.filters.offsetHeight
                    let viewportWidth = document.documentElement.offsetWidth
                    let viewportHeight = document.documentElement.offsetHeight
                    let filtersRight = viewportWidth - (filtersLeft + filtersWidth)
                    let filtersBottom = viewportHeight - (filtersTop + filtersHeight)
                    if (filtersRight <= 0) {
                        this.positionRight = "4px"
                    }
                    if (filtersBottom <= 0) {
                        this.positionBottom = "4px"
                    }
                }
            })
        },
        /**
         * Toggles a list or userList item on or off
         */
        toggleSelected (item) {
            // If data is a flat array
            if (this.dataIsAFlatArray) {
                let itemIndex = this.selected.indexOf(item)
                if (itemIndex > -1) this.selected.splice(itemIndex, 1)
                else this.selected.push(item)
                return
            }
            // If data is for a polymorphic relation
            if (item.hasOwnProperty("__model")) {
                let itemIndex = this.selected.findIndex(e => e.id === item.id && e.__model === item.__model)
                if (itemIndex > -1) this.selected.splice(itemIndex, 1)
                else this.selected.push({ id: item.id, __model: item.__model })

            // If data is anything else
            } else {
                let itemIndex = this.selected.indexOf(item[this.remap.id])
                if (itemIndex > -1) this.selected.splice(itemIndex, 1)
                else this.selected.push(item[this.remap.id])
            }
        },
        /**
         * Selects all options or unselects all options
         */
        selectAllOrNone () {
            if (this.areAllSelected) {
                this.selected = []
            } else {
                if (this.type === "list") {
                    // Handle polymorphic relation options
                    if (this.data.length > 0 && this.data[0] && this.data[0].__model) {
                        this.selected = this.data.map(e => { return { id: e.id, __model: e.__model } })
                        return
                    // Handle array of objects options
                    } else if (this.data.length > 0 && this.data[0] && this.data[0][this.remap.id] !== undefined) {
                        this.selected = this.data.map(e => e[this.remap.id])
                        return
                    }
                    // Handle array of ids options
                    this.selected = [...this.data]
                    return
                }
                if (this.type === "userList") {
                    this.selected = this.data.map(e => e[this.remap.id])
                }
            }
        }
    }
}
</script>

<style lang="scss">
.uik-filters {
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    max-width: 100%;
    .uik-filters__container {
        display: flex;
        flex-flow: column nowrap;
        justify-content: flex-start;
        align-items: flex-start;
        background: white;
        border-radius: 7px;
        border: $borderDefault;
        box-shadow: $boxShadow;
        position: fixed;
        z-index: 11;
        max-width: 100%;
        min-width: 400px;
        margin-top: 10px;
        .uik-filters__head {
            display: flex;
            flex-flow: row nowrap;
            justify-content: flex-start;
            align-items: center;
            border-bottom: $borderDefault;
            width: 100%;
            padding: 18px 20px;
            h1 {
                transform: translateY(1px);
                font-size: 15px;
                line-height: 20px;
                color: $textMain;
                font-weight: 500;
                white-space: nowrap;
                max-width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                margin: 0;
            }
            button {
                display: flex;
                flex-flow: row nowrap;
                justify-content: center;
                align-items: center;
                background: transparent;
                border: none;
                padding: 5px;
                font-size: 19px;
                color: darken($borderColorDarker, 15%);
                margin-top: 0;
                position: absolute;
                right: 15px;
                transition: all 0.15s;
                &::after {
                    content: "";
                    position: absolute;
                    width: 41px;
                    height: 41px;
                    background: $borderColor;
                    border-radius: 50%;
                    z-index: 1;
                    transition: all 0.15s;
                    transform: scale(0.5);
                    opacity: 0;
                }
                svg {
                    position: relative;
                    z-index: 2;
                }
                &:hover {
                    cursor: pointer;
                    color: $primary;
                    &::after {
                        opacity: 1;
                        transform: none;
                    }
                }
                &:active {
                    &::after {
                        opacity: 0.5;
                        transform: scale(0.8);
                    }
                }
                &.uik-filters__close-btn {
                    display: none;
                    font-size: 26px;
                    padding: 5px 10px;
                    right: 10px;
                    &::after {
                        width: 42px;
                        height: 42px;
                    }
                }
            }
            .uik-checkbox__wrapper {
                text-align: left;
                padding: 0;
                display: block;
                position: relative;
                margin-right: 13px;
                &:hover {
                    cursor: pointer;
                    .uik-checkbox__label {
                        &::before {
                            border-color: $borderColorDarker;
                            background: rgba($borderColor, 0.5);
                        }
                    }
                }
                .uik-checkbox__label {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    &::before {
                        width: 18px;
                        height: 18px;
                        min-width: 18px;
                        left: 0;
                        margin-right: 0;
                        z-index: 1;
                        top: 1px;
                        transform: none;
                        animation: uik-filters-unchecked 0.25s;
                        @keyframes uik-filters-unchecked {
                            0% {
                                opacity: 0.5;
                                transform: scale(0.5)
                            }
                            100% {
                                opacity: 1;
                                transform: none;
                            }
                        }
                    }
                    svg {
                        position: absolute;
                        z-index: 2;
                        left: 5px;
                        top: 6px;
                        transform: scale(1.25);
                    }
                }
                .uik-checkbox__checkbox:checked + .uik-checkbox__label {
                    &::before {
                        animation: uik-filters-checked 0.15s;
                        @keyframes uik-filters-checked {
                            0% {
                                opacity: 0;
                                transform: scale(0)
                            }
                            100% {
                                opacity: 1;
                                transform: none;
                            }
                        }
                    }
                }
            }
        }
        .uik-filters__content {
            display: flex;
            flex-flow: column nowrap;
            justify-content: flex-start;
            align-items: flex-start;
            width: 100%;
            padding: 8px 15px 15px 15px;
            > * {
                width: 100%;
            }
            .uik-search {
                .uik-search__icon {
                    left: 9px;
                }
                button.uik-search__icon {
                    padding: 10px;
                    left: 0;
                    .uikon {
                        color: $textSecondary;
                    }
                    &:hover {
                        cursor: pointer;
                        .uikon {
                            color: $primary;
                        }
                    }
                }
                .uik-search__input {
                    padding: 5px 15px 5px 35px;
                    font-weight: 600;
                    &::placeholder {
                        font-weight: 400;
                        color: $textSecondary;
                    }
                }
            }
            .uik-filters__loading {
                height: 150px;
                display: flex;
                flex-flow: row nowrap;
                justify-content: center;
                align-items: center;
            }
            .uik-filters__content-wrapper {
                padding-top: 3px;
                display: flex;
                flex-flow: column nowrap;
                justify-content: flex-start;
                align-items: flex-start;
                width: 100%;
                max-height: 40vh;
                overflow: auto;
                &::-webkit-scrollbar-track {
                    display: initial;
                }
                &::-webkit-scrollbar {
                    display: initial;
                    border-left: none;
                }
                > * {
                    width: 100%;
                }
                .uik-checkbox__wrapper {
                    text-align: left;
                    padding: 6px 7px;
                    display: block;
                    position: relative;
                    border-radius: 4px;
                    transition: all 0.2s;
                    &:hover {
                        transition: all 0.1s;
                        cursor: pointer;
                        background: $borderColor;
                        .uik-checkbox__label {
                            color: $textMain !important;
                        }
                    }
                    &:active {
                        transition: all 0.2s;
                        background: rgba($borderColor, 0.5);
                    }
                    .uik-checkbox__label {
                        display: block;
                        align-items: center;
                        white-space: nowrap;
                        max-width: 350px;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        padding-left: 28px;
                        font-size: 15px;
                        line-height: 17px;
                        height: 20px;
                        padding-top: 2px;
                        font-weight: 500;
                        transition: all 0.15s;
                        &::before {
                            position: absolute;
                            width: 18px;
                            height: 18px;
                            min-width: 18px;
                            left: 0;
                            margin-right: 0;
                            z-index: 1;
                            top: 1px;
                            transform: none;
                            animation: uik-filters-unchecked 0.25s;
                            @keyframes uik-filters-unchecked {
                                0% {
                                    opacity: 0.5;
                                    transform: scale(0.5)
                                }
                                100% {
                                    opacity: 1;
                                    transform: none;
                                }
                            }
                        }
                        svg {
                            position: absolute;
                            z-index: 2;
                            left: 5px;
                            top: 7px;
                            transform: scale(1.25);
                        }
                    }
                    .uik-checkbox__checkbox:checked + .uik-checkbox__label {
                        &::before {
                            animation: uik-filters-checked 0.15s;
                            @keyframes uik-filters-checked {
                                0% {
                                    opacity: 0;
                                    transform: scale(0)
                                }
                                100% {
                                    opacity: 1;
                                    transform: none;
                                }
                            }
                        }
                    }
                    .uik-checkbox__description {
                        margin-left: 28px;
                        margin-top: -2px;
                        font-size: 13px;
                    }
                    &.selected {
                        background: rgba($borderColor, 0.5);
                        &:hover {
                            background: $borderColor;
                        }
                    }
                }
                .uik-filters__user-select-label {
                    padding: 6px 7px;
                    transition: all 0.2s;
                    border-radius: 4px;
                    .uik-avatar__wrapper {
                        .uik-avatar__avatarWrapper {
                            .uik-avatar__avatar {
                                border: solid 0px transparent;
                                transition: all 0.25s;
                            }
                            .uik-avatar__avatarPlaceholder {
                                background: white;
                                border-color: #DFE3E9;
                                font-size: 11px;
                                line-height: 1;
                                font-weight: 400;
                                transition: all 0.25s;
                            }
                            animation: uik-filters-user-unchecked 0.25s;
                            @keyframes uik-filters-user-unchecked {
                                0% {
                                    transform: scale(0.5)
                                }
                                100% {
                                    transform: none;
                                }
                            }
                        }
                        .uik-avatar__info {
                            margin-left: 10px;
                            .uik-avatar__name {
                                font-weight: 500;
                                color: $textBase;
                                white-space: nowrap;
                                overflow: hidden;
                                text-overflow: ellipsis;
                                max-width: 316px;
                                display: block;
                                text-align: left;
                            }
                        }
                    }
                    &:hover {
                        transition: all 0.1s;
                        cursor: pointer;
                        background: $borderColor;
                        .uik-avatar__wrapper {
                            .uik-avatar__info {
                                .uik-avatar__name {
                                    color: $textMain;
                                }
                            }
                        }
                    }
                    &:active {
                        transition: all 0.2s;
                        background: rgba($borderColor, 0.5);
                    }
                    &.selected {
                        background: rgba($borderColor, 0.5);
                        &:hover {
                            background: $borderColor;
                        }
                        .uik-avatar__wrapper {
                            .uik-avatar__avatarWrapper {
                                background: $primary;
                                border-radius: 50%;
                                .uik-avatar__avatar {
                                    border: solid 2px $primary;
                                    opacity: 0.9;
                                }
                                .uik-avatar__avatarPlaceholder {
                                    border-color: $primary;
                                    color: white;
                                    background: $primary;
                                }
                                animation: uik-filters-user-checked 0.25s;
                                @keyframes uik-filters-user-checked {
                                    0% {
                                        transform: scale(0.5)
                                    }
                                    100% {
                                        transform: none;
                                    }
                                }
                            }
                            .uik-avatar__info {
                                .uik-avatar__name {
                                    color: $textMain;
                                }
                            }
                        }
                    }
                }
                .uik-filters__date {
                    margin: 0;
                    padding: 4px 0;
                    .uik-datePicker__inline-active-date {
                        display: none;
                    }
                    .mx-datepicker {
                        > * {
                            border-radius: 5px;
                            box-shadow: $boxShadow;
                        }
                        > *:last-child {
                            margin-left: 10px;
                            border-left: solid 1px $borderColor;
                        }
                    }
                    @media only #{$mediaTablet} {
                        padding: 4px;
                        .mx-datepicker {
                            flex-flow: column nowrap;
                            > *:last-child {
                                margin-left: 0;
                                margin-top: 12px;
                            }
                            .mx-calendar-content {
                                width: 100%;
                            }
                        }
                    }
                }
            }
            .uik-filters__bottom {
                width: 100%;
                padding-top: 10px;
                .uik-btn__base {
                    border-radius: 5px;
                    width: 100%;
                    .uik-btn__content {
                        font-size: 14px;
                        font-weight: 400;
                        letter-spacing: 0.4px;
                        .uikon {
                            opacity: 1;
                            font-size: 9px;
                        }
                    }
                }
            }
        }
    }
    .uik-overlay {
        z-index: 10;
    }
    .fade-enter-active {
        transition: opacity 0.35s !important;
    }
    .fade-leave-active {
        transition: opacity 0s !important;
    }
    .fade-enter, .fade-leave-to {
        opacity: 0 !important;
    }
    @media only #{$mediaMobile} {
        .uik-filters__container {
            width: 100%;
            min-width: unset;
            right: 0 !important;
            left: 0;
            height: 100%;
            top: 0;
            margin-top: 0;
            max-height: 100%;
            border: none;
            border-radius: 0;
            .uik-filters__head {
                padding: 14px 100px 14px 15px;
                background: $bg;
                .uik-checkbox__wrapper {
                    margin-right: 11px;
                }
                button {
                    width: 26px;
                    height: 26px;
                    padding: 0 !important;
                    background: white;
                    border: $borderDefault;
                    border-radius: 4px;
                    color: $textBase;
                    &::after {
                        display: none;
                    }
                    &.uik-filters__reset-btn {
                        right: 42px;
                        font-size: 18px;
                        border-color: transparent;
                        background: transparent;
                        color: $textSecondary;
                        width: 32px;
                        height: 32px;
                        &:hover {
                            color: $textMain;
                        }
                    }
                    &.uik-filters__close-btn {
                        font-size: 14px;
                        display: flex;
                        right: 11px;
                        &:hover {
                            color: $textBase;
                        }
                    }
                    &:hover {
                        transform: scale(0.85);
                    }
                }
            }
            .uik-filters__content {
                padding: 6px 10px 10px 10px;
                max-height: calc(100% - 49px);
                height: calc(100% - 49px);
                .uik-filters__content-wrapper {
                    padding-top: 1px;
                    max-height: 100%;
                    height: 100%;
                    &::-webkit-scrollbar-track {
                        display: none;
                    }
                    &::-webkit-scrollbar {
                        display: none;
                    }
                    .uik-checkbox__wrapper, .uik-filters__user-select-label {
                        &:hover {
                            background: transparent;
                        }
                        &.selected:hover {
                            background: rgba($borderColor, 0.5);
                        }
                    }
                }
            }
            animation: uik-filters-mobile 0.5s;
            @keyframes uik-filters-mobile {
                from {
                    opacity: 1;
                    transform: translateY(100%);
                }
                to {
                    opacity: 1;
                    transform: none;
                }
            }
        }
    }
}
// Override !important rule for checkboxes in table
#posts .table .uik-filters .uik-checkbox__label::before {
    margin-right: 10px !important;
}
</style>
